import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Crown, Shield, Users, UserCog, AlertTriangle, CheckCircle } from "lucide-react";
import { useOptimizedAuth } from "@/hooks/useOptimizedAuth";
import { toast } from "sonner";

interface User {
  user_id: string;
  email: string;
  full_name: string;
  role: string;
  created_at: string;
  updated_at: string;
  last_sign_in_at: string;
}

interface DatabaseResponse {
  success: boolean;
  message?: string;
  error?: string;
  user_id?: string;
  email?: string;
  new_role?: string;
}

const UserManagement = () => {
  const [promoteEmail, setPromoteEmail] = useState("");
  const [demoteEmail, setDemoteEmail] = useState("");
  const [newRole, setNewRole] = useState("admin");
  const [isPromoteDialogOpen, setIsPromoteDialogOpen] = useState(false);
  const [isDemoteDialogOpen, setIsDemoteDialogOpen] = useState(false);
  
  const { isSuperAdmin } = useOptimizedAuth();
  const queryClient = useQueryClient();

  // Fetch all users
  const { data: users, isLoading } = useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_all_users_for_admin');
      if (error) throw error;
      return data as User[];
    },
  });

  // Promote user mutation
  const promoteMutation = useMutation({
    mutationFn: async (email: string) => {
      const { data, error } = await supabase.rpc('promote_user_to_super_admin', {
        p_user_email: email
      });
      if (error) throw error;
      return data as unknown as DatabaseResponse;
    },
    onSuccess: (result) => {
      if (result.success) {
        toast.success(result.message || 'User promoted successfully');
        queryClient.invalidateQueries({ queryKey: ['admin-users'] });
        setPromoteEmail("");
        setIsPromoteDialogOpen(false);
      } else {
        toast.error(result.error || 'Failed to promote user');
      }
    },
    onError: (error) => {
      toast.error(`Failed to promote user: ${error.message}`);
    }
  });

  // Demote user mutation
  const demoteMutation = useMutation({
    mutationFn: async ({ email, role }: { email: string; role: string }) => {
      const { data, error } = await supabase.rpc('demote_super_admin', {
        p_user_email: email,
        p_new_role: role
      });
      if (error) throw error;
      return data as unknown as DatabaseResponse;
    },
    onSuccess: (result) => {
      if (result.success) {
        toast.success(result.message || 'User demoted successfully');
        queryClient.invalidateQueries({ queryKey: ['admin-users'] });
        setDemoteEmail("");
        setIsDemoteDialogOpen(false);
      } else {
        toast.error(result.error || 'Failed to demote user');
      }
    },
    onError: (error) => {
      toast.error(`Failed to demote user: ${error.message}`);
    }
  });

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'super_admin':
        return <Crown className="h-4 w-4 text-yellow-500" />;
      case 'admin':
        return <Shield className="h-4 w-4 text-blue-500" />;
      case 'manager':
        return <UserCog className="h-4 w-4 text-green-500" />;
      default:
        return <Users className="h-4 w-4 text-gray-500" />;
    }
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'default';
      case 'admin':
        return 'secondary';
      case 'manager':
        return 'outline';
      default:
        return 'secondary';
    }
  };

  if (!isSuperAdmin) {
    return (
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Only Super Admins can manage user roles and promote other users.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-yellow-500" />
              Promote to Super Admin
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Grant super admin privileges to a user. Use this carefully as super admins have full system access.
            </p>
            <Dialog open={isPromoteDialogOpen} onOpenChange={setIsPromoteDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Crown className="h-4 w-4 mr-2" />
                  Promote User
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Promote User to Super Admin</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      <strong>Warning:</strong> Super admins have complete system access including the ability to promote/demote other users.
                    </AlertDescription>
                  </Alert>
                  <div>
                    <Label htmlFor="promote-email">User Email</Label>
                    <Input
                      id="promote-email"
                      type="email"
                      value={promoteEmail}
                      onChange={(e) => setPromoteEmail(e.target.value)}
                      placeholder="user@example.com"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => promoteMutation.mutate(promoteEmail)}
                      disabled={!promoteEmail || promoteMutation.isPending}
                    >
                      {promoteMutation.isPending ? "Promoting..." : "Promote to Super Admin"}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setIsPromoteDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-500" />
              Demote Super Admin
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Remove super admin privileges from a user. The system prevents removing the last super admin.
            </p>
            <Dialog open={isDemoteDialogOpen} onOpenChange={setIsDemoteDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Shield className="h-4 w-4 mr-2" />
                  Demote User
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Demote Super Admin</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="demote-email">Super Admin Email</Label>
                    <Input
                      id="demote-email"
                      type="email"
                      value={demoteEmail}
                      onChange={(e) => setDemoteEmail(e.target.value)}
                      placeholder="superadmin@example.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="new-role">New Role</Label>
                    <Select value={newRole} onValueChange={setNewRole}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="customer">Customer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="destructive"
                      onClick={() => demoteMutation.mutate({ email: demoteEmail, role: newRole })}
                      disabled={!demoteEmail || demoteMutation.isPending}
                    >
                      {demoteMutation.isPending ? "Demoting..." : "Demote User"}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setIsDemoteDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      </div>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Users</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div>Loading users...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Last Sign In</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users?.map((user) => (
                  <TableRow key={user.user_id}>
                    <TableCell className="font-medium">{user.email}</TableCell>
                    <TableCell>{user.full_name || 'N/A'}</TableCell>
                    <TableCell>
                      <Badge variant={getRoleBadgeVariant(user.role)} className="flex items-center gap-1 w-fit">
                        {getRoleIcon(user.role)}
                        {user.role.replace('_', ' ').toUpperCase()}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(user.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {user.last_sign_in_at 
                        ? new Date(user.last_sign_in_at).toLocaleDateString()
                        : 'Never'
                      }
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UserManagement;